import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { selectUnitId } from '@homeplay/app';
import OnboardingContext from '../../context/onboardingContext';
import {
  AUTO_START_DELAY_MS,
  ONBOARDING_STEPS,
  getOnboardingStorageKey,
} from './onboarding.constants';

const hasCompletedOnboarding = (storageKey) => {
  if (typeof window === 'undefined') return true;
  if (!storageKey) return true;
  try {
    return window.localStorage.getItem(storageKey) === 'true';
  } catch {
    return true;
  }
};

const persistCompletion = (storageKey) => {
  if (typeof window === 'undefined') return;
  if (!storageKey) return;
  try {
    window.localStorage.setItem(storageKey, 'true');
  } catch {
    /* ignore */
  }
};

const clearCompletion = (storageKey) => {
  if (typeof window === 'undefined') return;
  if (!storageKey) return;
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    /* ignore */
  }
};

const OnboardingProvider = ({ children, autoStart = true }) => {
  const [isActive, setIsActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const targetsRef = useRef(new Map());

  const userId = useSelector(selectUnitId);
  const storageKey = useMemo(
    () => (userId != null ? getOnboardingStorageKey(userId) : null),
    [userId]
  );

  const start = useCallback(() => {
    setStepIndex(0);
    setIsActive(true);
  }, []);

  const complete = useCallback(() => {
    persistCompletion(storageKey);
    setIsActive(false);
  }, [storageKey]);

  const restart = useCallback(() => {
    clearCompletion(storageKey);
    start();
  }, [start, storageKey]);

  const next = useCallback(() => {
    setStepIndex((prev) => {
      if (prev >= ONBOARDING_STEPS.length - 1) {
        persistCompletion(storageKey);
        setIsActive(false);
        return prev;
      }
      return prev + 1;
    });
  }, [storageKey]);

  const prev = useCallback(() => {
    setStepIndex((current) => (current > 0 ? current - 1 : current));
  }, []);

  const skip = useCallback(() => {
    complete();
  }, [complete]);

  const registerTarget = useCallback((id, el) => {
    if (el) {
      targetsRef.current.set(id, el);
    } else {
      targetsRef.current.delete(id);
    }
  }, []);

  const getTarget = useCallback(
    (id) => targetsRef.current.get(id) ?? null,
    []
  );

  useEffect(() => {
    if (!autoStart) return undefined;
    if (!storageKey) return undefined;
    if (hasCompletedOnboarding(storageKey)) return undefined;
    const timer = window.setTimeout(() => {
      start();
    }, AUTO_START_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [autoStart, start, storageKey]);

  const value = useMemo(
    () => ({
      isActive,
      stepIndex,
      stepCount: ONBOARDING_STEPS.length,
      start,
      restart,
      next,
      prev,
      skip,
      complete,
      registerTarget,
      getTarget,
    }),
    [
      isActive,
      stepIndex,
      start,
      restart,
      next,
      prev,
      skip,
      complete,
      registerTarget,
      getTarget,
    ]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingProvider;
